// Feasibility Service - Integrates feasibility scoring with existing search flow
// Connects government data sources with feasibility calculation engine

import config from '../config/api.js';
import { adaptToFeasibilityInput } from './FeasibilityAdapter.js';
import { validateFeasibilityInput } from '../schemas/feasibilitySchema.js';
import { isMeaningfulLandUse } from '../utils/landUse.js';

class FeasibilityService {
  
  /**
   * Calculate feasibility score for a property using existing government data
   * @param {Object} searchResults - Results from ArchitecturalPlansService
   * @param {Object} overrides - Manual overrides for any feasibility parameters
   * @param {Function} onStatusUpdate - Optional callback for status updates
   * @returns {Promise<Object>} Feasibility analysis results
   */
  static async calculateFeasibilityScore(searchResults, overrides = {}, onStatusUpdate = null) {
    try {
      if (onStatusUpdate) onStatusUpdate('מתחיל חישוב כדאיות...');
      console.log('🎯 Starting feasibility calculation for:', searchResults.address);
      
      // Extract relevant data from search results
      const parcelData = this.extractParcelData(searchResults);
      const buildingData = this.extractBuildingData(searchResults);
      const planningRights = this.extractPlanningRights(searchResults);
      
      console.log('📊 Extracted data:', {
        parcel: !!parcelData,
        building: !!buildingData,
        planning: !!planningRights
      });
      
      // Adapt government data to feasibility input format (now with real urban renewal detection AND building construction data!)
      const feasibilityInput = await adaptToFeasibilityInput(
        searchResults.address,
        parcelData,
        planningRights,
        {}, // PB status - now using real plan data instead!
        buildingData,
        [], // Comps - now using market data API instead
        overrides,
        searchResults.plans || [], // Pass the existing plans data!
        searchResults.coordinates, // Pass coordinates for building data lookup!
        onStatusUpdate // Pass the status update callback
      );
      
      // Validate the adapted input
      const validation = validateFeasibilityInput(feasibilityInput);
      if (!validation.success) {
        throw new Error(`Invalid feasibility input: ${JSON.stringify(validation.error)}`);
      }
      
      console.log('🚀 About to send to API - section7778Plans check:', {
        hasSection7778: !!feasibilityInput.section7778Plans,
        section7778PlansLength: feasibilityInput.section7778Plans?.length || 0,
        section7778Plans: feasibilityInput.section7778Plans
      });
      
      console.log('🚀 About to send to API - section23Plans check:', {
        hasSection23: !!feasibilityInput.section23Plans,
        section23PlansLength: feasibilityInput.section23Plans?.length || 0,
        section23Plans: feasibilityInput.section23Plans
      });
      
      // Call the feasibility API endpoint
      if (onStatusUpdate) onStatusUpdate('מחשב כדאיות פרויקט...');
      const response = await fetch(`${config.apiBaseUrl}/api/feasibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(feasibilityInput)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Feasibility API error: ${response.status} - ${errorData.message || response.statusText}`);
      }
      
      const feasibilityResult = await response.json();
      
      console.log('✅ Feasibility calculation completed:', {
        score: feasibilityResult.score0to100,
        program: feasibilityResult.programSuggested,
        notes: feasibilityResult.notes?.length || 0,
        hasBuildingInfo: !!feasibilityResult.buildingInfo,
        buildingInfo: feasibilityResult.buildingInfo
      });
      
      return {
        success: true,
        feasibilityInput,
        feasibilityResult,
        dataQuality: this.assessDataQuality(feasibilityInput, searchResults)
      };
      
    } catch (error) {
      console.error('❌ Feasibility calculation error:', error);
      return {
        success: false,
        error: error.message,
        feasibilityResult: null
      };
    }
  }
  
  /**
   * Extract parcel data from search results
   * @param {Object} searchResults - Search results object
   * @returns {Object|null} Parcel data
   */
  static extractParcelData(searchResults) {
    console.log('🔍 Extracting parcel data from searchResults:', {
      hasParcelData: !!searchResults.parcelData,
      hasCoordinates: !!searchResults.coordinates,
      hasParcelGeometry: !!searchResults.parcelGeometry,
      municipality: searchResults.municipality,
      address: searchResults.address,
      searchResults: searchResults
    });
    
    if (searchResults.parcelData) {
      // Extract municipality from address if not directly available
      let municipalityName = searchResults.municipality || 'לא ידוע';
      
      // If municipality is unknown, try to extract from address
      if (municipalityName === 'לא ידוע' && searchResults.address) {
        municipalityName = this.extractMunicipalityFromAddress(searchResults.address);
      }
      
      const parcelData = {
        x: searchResults.coordinates?.x,
        y: searchResults.coordinates?.y,
        // Also include parcel coordinates for municipal GIS (Israeli Grid)
        parcelX: searchResults.parcelGeometry?.centroid?.x,
        parcelY: searchResults.parcelGeometry?.centroid?.y,
        municipalityName: municipalityName,
        municipalityCode: searchResults.municipalityCode,
        gush: searchResults.parcelData.gushnumber || searchResults.gush,
        helka: searchResults.parcelData.parcelnumber || searchResults.helka,
        lotAreaSqm: searchResults.parcelData.lotArea // TODO: extract from geometry if available
      };
      
      console.log('✅ Extracted parcel data:', parcelData);
      return parcelData;
    }
    
    console.log('⚠️ No parcel data found in searchResults');
    return null;
  }
  
  /**
   * Extract municipality name from address string
   * @param {string} address - Full address string
   * @returns {string} Municipality name
   */
  static extractMunicipalityFromAddress(address) {
    const cityPatterns = [
      { patterns: ['תל אביב', 'תל-אביב', 'Tel Aviv', 'ת"א', 'יפו'], city: 'תל אביב-יפו' },
      { patterns: ['גבעתיים', 'Givataim'], city: 'גבעתיים' },
      { patterns: ['רמת גן', 'רמת-גן', 'Ramat Gan'], city: 'רמת גן' },
      { patterns: ['בת ים', 'בת-ים', 'Bat Yam'], city: 'בת ים' },
      { patterns: ['חולון', 'Holon'], city: 'חולון' },
      { patterns: ['ירושלים', 'Jerusalem', 'יירושלים'], city: 'ירושלים' },
      { patterns: ['חיפה', 'Haifa'], city: 'חיפה' },
      { patterns: ['באר שבע', 'באר-שבע', 'Beer Sheva'], city: 'באר שבע' },
      { patterns: ['פתח תקווה', 'פתח-תקווה', 'Petah Tikva'], city: 'פתח תקווה' },
      { patterns: ['נתניה', 'Netanya'], city: 'נתניה' },
      { patterns: ['ראשון לציון', 'ראשל"צ', 'Rishon LeZion'], city: 'ראשון לציון' },
      { patterns: ['רעננה', 'Ra\'anana'], city: 'רעננה' },
      { patterns: ['הרצליה', 'Herzliya'], city: 'הרצליה' },
      { patterns: ['כפר סבא', 'Kfar Saba'], city: 'כפר סבא' },
      { patterns: ['אשדוד', 'Ashdod'], city: 'אשדוד' },
      { patterns: ['אשקלון', 'Ashkelon'], city: 'אשקלון' },
      { patterns: ['רחובות', 'Rehovot'], city: 'רחובות' }
    ];
    
    for (const { patterns, city } of cityPatterns) {
      for (const pattern of patterns) {
        if (address.toLowerCase().includes(pattern.toLowerCase())) {
          console.log(`🏙️ Extracted municipality: ${city} from address: ${address}`);
          return city;
        }
      }
    }
    
    console.log(`⚠️ Could not extract municipality from address: ${address}`);
    return 'לא ידוע';
  }
  
  /**
   * Extract building data from search results
   * @param {Object} searchResults - Search results object
   * @returns {Object|null} Building data
   */
  static extractBuildingData(searchResults) {
    // Get land use from advancedLayers (govmap-layers) if available - works for all municipalities
    const advancedLayersLandUse = searchResults.advancedLayers?.landUse?.designation;
    
    // GovMap building stats as fallback for physical characteristics
    const buildingStats = searchResults.advancedLayers?.buildingStats || searchResults.propertyInfo?.advancedLayers?.buildingStats || null;
    const statsFloors = buildingStats ? (() => {
      const key = Object.keys(buildingStats).find(k => k.includes('קומות'));
      return key ? buildingStats[key] : null;
    })() : null;
    const statsHeight = buildingStats?.['גובה המבנה במטרים'] || null;
    // Prefer per-parcel clipped area over raw building complex area
    const parcelClip = searchResults.advancedLayers?.buildingAreaOnParcel || searchResults.propertyInfo?.advancedLayers?.buildingAreaOnParcel;
    const statsArea = (parcelClip?.isPartial ? parcelClip.buildingAreaOnParcel : null)
      || buildingStats?.['שטח במטרים רבועים'] || null;
    // Actual registered building use — distinguishes residential buildings on
    // commercially-designated parcels (mixed use) in the eligibility check.
    const buildingUseGroup = buildingStats?.['קבוצת שימוש'] || null;
    
    if (searchResults.buildingData) {
      console.log('🔍 FeasibilityService extractBuildingData - Raw buildingData:', searchResults.buildingData);
      console.log('🔍 FeasibilityService extractBuildingData - advancedLayers landUse:', advancedLayersLandUse);
      console.log('🔍 FeasibilityService extractBuildingData - buildingStats fallbacks:', { statsFloors, statsHeight, statsArea });
      const effectiveArea = (parcelClip?.isPartial ? parcelClip.buildingAreaOnParcel : null)
        || searchResults.buildingData.area || statsArea;
      const extracted = {
        floors: searchResults.buildingData.floors || statsFloors,
        height: searchResults.buildingData.height || statsHeight,
        area: effectiveArea,
        year: searchResults.buildingData.year,
        buildingType: searchResults.buildingData.buildingType,
        buildingNumber: searchResults.buildingData.buildingNumber,
        // Municipal detailed zoning (TA GIS layer 837) first: it reflects the
        // newest taba per migrash. GovMap retzefmigrashim is the fallback — it
        // can return stale 1940s designations, master-plan zones, or the road
        // polygon the geocoded point happens to land on. Placeholder values
        // ("לא ידוע" etc.) must not shadow a real GovMap designation.
        landUse:
          (isMeaningfulLandUse(searchResults.buildingData.landUse)
            ? searchResults.buildingData.landUse
            : null) || advancedLayersLandUse,
        buildingUseGroup,
        tama38Zone: searchResults.buildingData.tama38Zone,
        conservationSite: searchResults.buildingData.conservationSite,
        conservationArea: searchResults.buildingData.conservationArea
      };
      console.log('🔍 FeasibilityService extractBuildingData - Extracted:', extracted);
      return extracted;
    }
    
    // Even without buildingData, use landUse or building stats from advancedLayers
    if (advancedLayersLandUse || statsFloors || statsArea || statsHeight) {
      console.log('🔍 FeasibilityService extractBuildingData - Using advancedLayers fallback:', { statsFloors, statsHeight, statsArea, advancedLayersLandUse });
      return {
        floors: statsFloors,
        height: statsHeight,
        area: statsArea,
        landUse: advancedLayersLandUse,
        buildingUseGroup
      };
    }
    
    return null;
  }
  
  /**
   * Extract planning rights from search results
   * @param {Object} searchResults - Search results object
   * @returns {Object} Planning rights data
   */
  static extractPlanningRights(searchResults) {
    const planningRights = {};
    
    // TODO: Extract FAR, height limits, etc. from architectural plans
    // This would require parsing the plans data for zoning information
    
    if (searchResults.plans && searchResults.plans.length > 0) {
      // Look for approved plans that might indicate development rights
      const approvedPlans = searchResults.plans.filter(plan => 
        plan.status === 'approved' || plan.status === 'מאושר'
      );
      
      if (approvedPlans.length > 0) {
        // TODO: Parse plan details for FAR, height, etc.
        console.log(`📋 Found ${approvedPlans.length} approved plans - TODO: extract rights data`);
      }
    }
    
    return planningRights;
  }
  
  /**
   * Assess the quality of available data for feasibility calculation
   * @param {Object} feasibilityInput - The feasibility input
   * @param {Object} searchResults - Original search results
   * @returns {Object} Data quality assessment
   */
  static assessDataQuality(feasibilityInput, searchResults) {
    const quality = {
      overall: 'medium',
      details: {
        parcelData: searchResults.parcelData ? 'good' : 'missing',
        buildingConstructionData: 'enhanced', // NEW: Municipal building data integration!
        planningRights: 'estimated', // TODO: improve when we parse plans
        marketData: 'good', // Now using market data API!
        pre1980Eligibility: feasibilityInput.pre1980Eligibility === 'Unknown' ? 'estimated' : 'good',
        conservationStatus: 'enhanced', // NEW: Real conservation detection!
        tama38Policy: 'enhanced', // NEW: Real TAMA 38 policy extraction!
        bettermentLevy: 'enhanced' // NEW: Real betterment levy rates!
      },
      recommendations: []
    };
    
    // Generate recommendations for improving data quality
    if (!searchResults.parcelData) {
      quality.recommendations.push('חסרים נתוני גוש/חלקה מדויקים');
    }
    
    // Check if we got enhanced building data
    const municipalityName = feasibilityInput.municipalityName;
    if (municipalityName === 'תל אביב-יפו') {
      quality.recommendations.push('✅ נתוני בנייה מדויקים ממאגר היתרי הבנייה של תל אביב');
    } else if (municipalityName === 'ירושלים') {
      quality.recommendations.push('⚠️ אינטגרציה עם מאגר ירושלים בפיתוח - נתונים מוערכים');
    } else {
      quality.recommendations.push('⚠️ נתוני בנייה מוערכים - לא זמינים ממקורות עירוניים');
    }
    
    if (feasibilityInput.pre1980Eligibility === 'Unknown') {
      quality.recommendations.push('נדרש אישור שנת בנייה לזכאות תמ"א');
    } else {
      quality.recommendations.push('✅ זכאות תמ"א נקבעה על בסיס נתוני בנייה רשמיים');
    }
    
    // Enhanced data sources
    quality.recommendations.push('✅ מחירי השוק מבוססים על נתוני שוק עדכניים');
    quality.recommendations.push('✅ זיהוי שימור מתוך תוכניות קיימות במערכת');
    quality.recommendations.push('✅ מדיניות תמ"א 38 מתוך תוכניות iPLAN עדכניות');
    quality.recommendations.push('✅ שיעורי היטל השבחה ממקורות עירוניים');
    
    // Determine overall quality - now much better with enhanced data!
    const enhancedCount = Object.values(quality.details).filter(q => q === 'enhanced' || q === 'good').length;
    const totalCount = Object.values(quality.details).length;
    
    if (enhancedCount >= totalCount * 0.7) {
      quality.overall = 'excellent'; // New tier for enhanced data!
    } else if (enhancedCount >= totalCount * 0.5) {
      quality.overall = 'good';
    } else {
      quality.overall = 'medium';
    }
    
    return quality;
  }
  
  /**
   * Get feasibility score with custom parameters
   * @param {Object} customInput - Custom feasibility input
   * @returns {Promise<Object>} Feasibility results
   */
  static async calculateCustomFeasibility(customInput) {
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/feasibility`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(customInput)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Feasibility API error: ${response.status} - ${errorData.message || response.statusText}`);
      }
      
      const result = await response.json();
      return { success: true, feasibilityResult: result };
      
    } catch (error) {
      console.error('❌ Custom feasibility calculation error:', error);
      return { success: false, error: error.message };
    }
  }
}

export default FeasibilityService;
